import {
  isBinaryGrpcWebOrConnectContentType,
  isGrpcWebOrConnectContentType,
} from '@terra/shared-asset-processing';
import {
  BINARY_BODY_PREFIX,
  HAR_BASE64_COMMENT,
  encodeLatin1WireBodyToBase64,
  isGrpcWebVisibleBase64Payload,
  type BodyEncoding,
} from './har-body-encoding.utils';

const WIRE_SAMPLE_MAX_CHARS = 8_192;

export const DEFAULT_BOUNDED_WIRE_SAMPLE_CHARS = WIRE_SAMPLE_MAX_CHARS;

type HarPostDataLike = {
  mimeType?: string;
  text?: string;
  comment?: string;
  encoding?: BodyEncoding | string;
};

type HarResponseContentLike = {
  mimeType?: string;
  text?: string | null;
  comment?: string;
  encoding?: BodyEncoding | string;
};

export type BoundedWireSample = {
  direction: 'request' | 'response';
  encoding: 'utf8' | 'base64';
  data: string;
  truncated?: boolean;
  contentType?: string;
};

function capWireSampleData(data: string): {
  data: string;
  truncated: boolean;
} {
  if (data.length <= WIRE_SAMPLE_MAX_CHARS) {
    return { data, truncated: false };
  }

  return {
    data: data.slice(0, WIRE_SAMPLE_MAX_CHARS),
    truncated: true,
  };
}

function encodeBinaryGrpcField<T extends HarPostDataLike>(field: T): T {
  const mimeType = field.mimeType ?? '';
  const text = field.text;

  if (!text || !isBinaryGrpcWebOrConnectContentType(mimeType)) {
    return field;
  }

  if (
    field.encoding === 'base64' ||
    field.comment === HAR_BASE64_COMMENT ||
    text.startsWith(BINARY_BODY_PREFIX) ||
    isGrpcWebVisibleBase64Payload(text)
  ) {
    return {
      ...field,
      encoding: 'base64',
      comment: field.comment ?? HAR_BASE64_COMMENT,
    };
  }

  return {
    ...field,
    text: encodeLatin1WireBodyToBase64(text),
    encoding: 'base64',
    comment: HAR_BASE64_COMMENT,
  };
}

export function isDecodedGrpcJsonHarBody(text: string | undefined): boolean {
  if (!text) {
    return false;
  }

  const trimmed = text.trim().replace(/^\uFEFF/, '');

  if (!trimmed) {
    return false;
  }

  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

export function prepareGrpcHarRequestForPreview<
  T extends { postData?: HarPostDataLike },
>(request: T): T & { postData?: HarPostDataLike } {
  if (isDecodedGrpcJsonHarBody(request.postData?.text)) {
    return request;
  }

  return ensureBinarySafeGrpcHarRequest(request);
}

export function prepareGrpcHarResponseForPreview<
  T extends { content?: HarResponseContentLike },
>(response: T): T & { content?: HarResponseContentLike } {
  return ensureBinarySafeGrpcHarResponse(response);
}

export function ensureBinarySafeGrpcHarRequest<
  T extends { postData?: HarPostDataLike },
>(request: T): T & { postData?: HarPostDataLike } {
  if (!request.postData) {
    return request;
  }

  if (!isBinaryGrpcWebOrConnectContentType(request.postData.mimeType)) {
    return request;
  }

  return {
    ...request,
    postData: encodeBinaryGrpcField(request.postData),
  };
}

export function ensureBinarySafeGrpcHarResponse<
  T extends { content?: HarResponseContentLike },
>(response: T): T & { content?: HarResponseContentLike } {
  const content = response.content;

  if (!content || typeof content.text !== 'string') {
    return response;
  }

  if (!isBinaryGrpcWebOrConnectContentType(content.mimeType)) {
    return response;
  }

  return {
    ...response,
    content: {
      ...content,
      ...encodeBinaryGrpcField({
        mimeType: content.mimeType,
        text: content.text,
        comment: content.comment,
        encoding: content.encoding,
      }),
    },
  };
}

function pushBoundedSample({
  samples,
  direction,
  mimeType,
  text,
  comment,
  encoding,
}: {
  samples: BoundedWireSample[];
  direction: BoundedWireSample['direction'];
  mimeType?: string;
  text?: string | null;
  comment?: string;
  encoding?: BodyEncoding | string;
}): void {
  if (!text || !isGrpcWebOrConnectContentType(mimeType)) {
    return;
  }

  if (isBinaryGrpcWebOrConnectContentType(mimeType)) {
    const encoded = encodeBinaryGrpcField({
      mimeType,
      text,
      comment,
      encoding,
    });
    const capped = capWireSampleData(encoded.text ?? '');

    samples.push({
      direction,
      encoding: 'base64',
      data: capped.data,
      ...(capped.truncated ? { truncated: true } : {}),
      ...(encoded.mimeType ? { contentType: encoded.mimeType } : {}),
    });

    return;
  }

  const capped = capWireSampleData(text);

  samples.push({
    direction,
    encoding: 'utf8',
    data: capped.data,
    ...(capped.truncated ? { truncated: true } : {}),
    ...(mimeType ? { contentType: mimeType } : {}),
  });
}

export function buildBoundedGrpcWireSamples({
  requestPostData,
  responseContent,
}: {
  requestPostData?: HarPostDataLike;
  responseContent?: HarResponseContentLike;
}): BoundedWireSample[] {
  const samples: BoundedWireSample[] = [];

  pushBoundedSample({
    samples,
    direction: 'request',
    mimeType: requestPostData?.mimeType,
    text: requestPostData?.text,
    comment: requestPostData?.comment,
    encoding: requestPostData?.encoding,
  });

  pushBoundedSample({
    samples,
    direction: 'response',
    mimeType: responseContent?.mimeType,
    text: responseContent?.text,
    comment: responseContent?.comment,
    encoding: responseContent?.encoding,
  });

  return samples;
}
